import { useEffect, useState } from "react";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Pencil, Wrench, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

const STATUS = [
  { value: "declare", label: "Déclaré", color: "bg-slate-100 text-slate-700" },
  { value: "en_cours", label: "En cours", color: "bg-blue-50 text-blue-800" },
  { value: "resolu", label: "Résolu", color: "bg-emerald-100 text-emerald-800" },
];
const PRIORITY = [
  { value: "basse", label: "Basse", color: "bg-slate-100 text-slate-700" },
  { value: "moyenne", label: "Moyenne", color: "bg-amber-100 text-amber-800" },
  { value: "haute", label: "Haute", color: "bg-rose-100 text-rose-800" },
];

async function fileToDataUrl(file) {
  const r = new FileReader();
  return new Promise((res, rej) => { r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
}

const empty = {
  property_id: "", lease_id: null, title: "", description: "",
  status: "declare", priority: "moyenne",
  reported_date: new Date().toISOString().slice(0, 10), resolved_date: null,
  contractor_name: "", contractor_contact: "", cost: 0, photos: [],
};

export default function IncidentsPage() {
  const { user } = useAuth();
  const { canWrite } = planContext(user);
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [i, p] = await Promise.all([api.get("/incidents"), api.get("/properties")]);
    setItems(i.data); setProps(p.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { if (!canWrite) return; setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = async (id) => {
    if (!canWrite) return;
    const { data } = await api.get(`/incidents/${id}`);
    setForm({
      ...empty, ...data,
      description: data.description || "",
      contractor_name: data.contractor_name || "",
      contractor_contact: data.contractor_contact || "",
      cost: data.cost ?? 0,
      photos: data.photos || [],
    });
    setEditId(id); setOpen(true);
  };

  const addPhotos = async (files) => {
    const dataUrls = [];
    for (const f of files) {
      if (f.size > 3 * 1024 * 1024) { toast.error(`${f.name} > 3 Mo`); continue; }
      dataUrls.push(await fileToDataUrl(f));
    }
    setForm({ ...form, photos: [...(form.photos || []), ...dataUrls] });
  };

  const save = async () => {
    if (!canWrite) return;
    try {
      const payload = {
        ...form,
        cost: Number(form.cost || 0),
        lease_id: form.lease_id || null,
        resolved_date: form.resolved_date || null,
      };
      if (editId) { await api.put(`/incidents/${editId}`, payload); toast.success("Incident mis à jour"); }
      else { await api.post("/incidents", payload); toast.success("Incident déclaré"); }
      setOpen(false); load();
    } catch (e) { toast.error("Erreur : " + (e.response?.data?.detail || e.message)); }
  };

  const remove = async (id) => {
    if (!canWrite) return;
    if (!window.confirm("Supprimer cet incident ?")) return;
    await api.delete(`/incidents/${id}`); load();
  };

  const statusBadge = (s) => {
    const m = STATUS.find((x) => x.value === s) || STATUS[0];
    return <Badge className={`${m.color} border-0`}>{m.label}</Badge>;
  };
  const priorityBadge = (s) => {
    const m = PRIORITY.find((x) => x.value === s) || PRIORITY[1];
    return <Badge variant="outline" className="capitalize">{m.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Incidents & Maintenance</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} incident(s) au total.</p>
        </div>
        <Button data-testid="incident-new-btn" onClick={openNew} disabled={!canWrite} className="bg-[hsl(var(--primary))]">
          <Plus className="h-4 w-4 mr-2" /> Nouvel incident
        </Button>
      </div>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Wrench className="h-10 w-10 mx-auto text-slate-300" />
            <div className="mt-3 text-sm text-slate-500">Aucun incident.</div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Bien</TableHead>
                <TableHead>Priorité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Coût</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} data-testid={`incident-row-${i.id}`}>
                  <TableCell className="text-sm">{fmtDate(i.reported_date)}</TableCell>
                  <TableCell>
                    <div className="font-medium">{i.title}</div>
                    {i.contractor_name && <div className="text-xs text-slate-500">Artisan : {i.contractor_name}</div>}
                  </TableCell>
                  <TableCell className="text-sm">{i.property_label}</TableCell>
                  <TableCell>{priorityBadge(i.priority)}</TableCell>
                  <TableCell>{statusBadge(i.status)}</TableCell>
                  <TableCell className="text-sm">{fmtEUR(i.cost || 0)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => openEdit(i.id)}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" disabled={!canWrite} onClick={() => remove(i.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifier l'incident" : "Nouvel incident"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Bien concerné</Label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger data-testid="incident-property"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.label} — {p.city}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Titre</Label>
              <Input data-testid="incident-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Fuite d'eau salle de bain" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <Label>Date de déclaration</Label>
              <Input type="date" value={form.reported_date} onChange={(e) => setForm({ ...form, reported_date: e.target.value })} />
            </div>
            <div>
              <Label>Date de résolution</Label>
              <Input type="date" value={form.resolved_date || ""} onChange={(e) => setForm({ ...form, resolved_date: e.target.value })} />
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="incident-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priorité</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITY.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Artisan / prestataire</Label>
              <Input value={form.contractor_name} onChange={(e) => setForm({ ...form, contractor_name: e.target.value })} />
            </div>
            <div>
              <Label>Contact artisan</Label>
              <Input value={form.contractor_contact} onChange={(e) => setForm({ ...form, contractor_contact: e.target.value })} />
            </div>
            <div>
              <Label>Coût (€)</Label>
              <Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Photos</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(form.photos || []).map((p, i) => (
                  <div key={i} className="relative group">
                    <img src={p} alt="" className="h-20 w-20 object-cover rounded-md border border-slate-200" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, photos: form.photos.filter((_, x) => x !== i) })}
                      className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full h-5 w-5 flex items-center justify-center opacity-0 group-hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <label className="h-20 w-20 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center cursor-pointer hover:bg-slate-50">
                  <ImageIcon className="h-5 w-5 text-slate-400" />
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => addPhotos(Array.from(e.target.files))} />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="incident-save" onClick={save} className="bg-[hsl(var(--primary))]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
